<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import TenderHeader from './TenderHeader.vue'
import TenderOverView from './TenderOverView.vue'
import TenderAgentAssignment from './TenderAgentAssignment.vue'
import TenderStatus from './TenderStatus.vue'
import TenderEngineerView from './TenderEngineerView.vue'
import type { Tender, WorkflowStatus } from '@/types/tender'
import api from '@/services/api'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import { notificationService } from '@/services/notificationService'
import { useAuthStore } from '@/stores/auth'
import { AxiosError } from 'axios'
import TenderAuditTrail from './TenderAuditTrail.vue'
import TenderComplianceChecklist from './TenderComplianceChecklist.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import RefreshIcon from '@/icons/RefreshIcon.vue'
import type { CompanyType } from '@/types/user'
import { tenderReportService } from '@/services/tenderReportService'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const tenderId = computed(() => route.params.id as string)
const tender = ref<Tender | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

// Get user's company from auth store
const userCompany = computed<CompanyType | undefined>(() => {
  const user = authStore.user
  if (!user) return undefined

  if ('company' in user) {
    return (user as { company?: CompanyType }).company
  }
  return undefined
})

// Check if engineer is assigned to any consignee in this tender
const isEngineerAssignedToConsignee = async (tenderId: string, engineerId: string): Promise<boolean> => {
  try {
    // Check if tenderId exists
    if (!tenderId) {
      console.warn('No tenderId provided to isEngineerAssignedToConsignee')
      return false
    }

    const report = await tenderReportService.getReportByTenderId(tenderId)
    if (!report || !report.consignees) return false

    return report.consignees.some(consignee => consignee.assignedEngineerId === engineerId)
  } catch (error) {
    console.error('Error checking engineer assignment:', error)
    return false
  }
}

// Check if user has access to this tender
const checkTenderAccess = async (tenderData: Tender): Promise<boolean> => {
  const userType = authStore.user?.userType
  const userId = authStore.user?.id

  // Admins can access all tenders
  if (userType === 'admin') {
    return true
  }

  // Agents can only access tenders assigned to them
  if (userType === 'agent') {
    const isAssignedToMe = tenderData.assignedToAgent === userId
    if (!isAssignedToMe) {
      notificationService.warning('This tender is not assigned to you.', 3000)
      return false
    }
    return true
  }

  // Engineers can access if assigned to any consignee in this tender
  if (userType === 'engineer') {
    // Check if userId exists
    if (!userId) {
      notificationService.warning('User not authenticated.', 3000)
      return false
    }

    // Check if tenderData.id exists
    if (!tenderData.id) {
      notificationService.warning('Invalid tender data.', 3000)
      return false
    }

    const isAssigned = await isEngineerAssignedToConsignee(tenderData.id, userId)
    if (!isAssigned) {
      notificationService.warning('You are not assigned to any consignee for this tender.', 3000)
      return false
    }
    return true
  }

  // Default deny access for other user types
  notificationService.warning('You do not have permission to view this tender.', 3000)
  return false
}

// Fetch tender details from API
const fetchTenderDetails = async () => {
  try {
    loading.value = true
    error.value = null
    console.log(`📥 Fetching tender details for ID: ${tenderId.value}`)

    // Use the correct endpoint
    const response = await api.get(`/api/tenders/id/${tenderId.value}`)

    if (response.data) {
      tender.value = response.data
      console.log('✅ Tender details fetched successfully:', response.data)

      // Check if user has access to this tender
      const hasAccess = await checkTenderAccess(response.data)
      if (!hasAccess) {
        // Redirect to dashboard if no access
        router.push('/dashboard')
        return
      }
    } else {
      throw new Error('No tender data found')
    }
  } catch (err: unknown) {
    console.error('❌ Failed to fetch tender details:', err)

    // Type guard to check if it's an AxiosError
    const axiosError = err as AxiosError<{message?: string}>

    // Handle 403 Forbidden error from backend
    if (axiosError.response?.status === 403) {
      notificationService.warning('Access denied. This tender is not assigned to you.', 3000)
      router.push('/dashboard')
      return
    }

    // Handle 404 Not Found
    if (axiosError.response?.status === 404) {
      error.value = 'Tender not found. It may have been deleted or does not exist.'
      notificationService.error('Tender not found')
      return
    }

    // Handle other errors
    const errorMessage = axiosError.response?.data?.message ||
                        axiosError.message ||
                        'Failed to load tender details. Please try again.'
    error.value = errorMessage
    notificationService.error('Failed to load tender details')
  } finally {
    loading.value = false
  }
}

// Update tender status - now with proper type handling
const updateTenderStatus = async (status: Tender['status']) => {
  if (!tender.value) return

  try {
    console.log(`📝 Updating tender status to: ${status}`)

    const response = await api.put(`/api/tenders/update-status`, {
      tenderIds: [tender.value.t247Id],
      status: status,
      updatedAt: new Date().toISOString()
    })

    if (response.data.success) {
      tender.value.status = status
      notificationService.success('Tender status updated successfully!')

      // Refresh the tender data
      await fetchTenderDetails()
    }
  } catch (err) {
    console.error('❌ Failed to update tender status:', err)
    notificationService.error('Failed to update tender status')
  }
}

// Handle agent assignment
const handleAgentAssignment = async (assignmentData: {
  tenderId: string
  agentId: string
  notes: string
}) => {
  try {
    console.log('Agent assigned:', assignmentData)

    // Call the API to assign agent
    const response = await api.put('/api/tenders/assign-agent', {
      tenderId: assignmentData.tenderId,
      agentId: assignmentData.agentId,
      notes: assignmentData.notes,
      workflowStatus: 'assigned-to-agent'
    })

    if (response.data.success) {
      notificationService.success('Agent assigned successfully!')

      // Update local state
      if (tender.value) {
        tender.value.assignedToAgent = assignmentData.agentId
        tender.value.workflowStatus = 'assigned-to-agent'
        // Optionally update status too
        tender.value.status = 'assigned-to-agent'
      }

      // Refresh the tender data
      await fetchTenderDetails()
    }
  } catch (err) {
    console.error('❌ Failed to handle agent assignment:', err)
    notificationService.error('Failed to assign agent')
  }
}

// Define proper interface for workflow payload
interface WorkflowUpdatePayload {
  workflowStatus: NonNullable<WorkflowStatus>
  userType?: string
  updatedBy?: string
  notes?: string
  priceReviewNotes?: string
  criteriaReviewNotes?: string
  wonNotes?: string
  lostNotes?: string
  tenderCompletedNotes?: string
  underQueryNotes?: string
}

const handleWorkflowUpdate = async (
  workflowStatus: NonNullable<WorkflowStatus>,
  notes?: string,
  reviewData?: {
    underQueryNotes?: string
    priceReviewNotes?: string
    criteriaReviewNotes?: string
    wonNotes?: string
    lostNotes?: string
    tenderCompletedNotes?: string
  }
) => {
  if (!tender.value) return

  try {
    console.log(`📝 Updating workflow status to: ${workflowStatus}`)
    console.log('📋 Notes:', notes)
    console.log('📋 Review data:', reviewData)

    const payload: WorkflowUpdatePayload = {
      workflowStatus,
      userType: authStore.user?.userType,
      updatedBy: authStore.user?.id
    }

    // Add notes based on workflow status
    if (notes && notes.trim()) {
      payload.notes = notes
    }

    // Add ALL review data fields to the payload
    if (reviewData) {
      if (reviewData.underQueryNotes) {
        payload.underQueryNotes = reviewData.underQueryNotes
      }
      if (reviewData.priceReviewNotes) {
        payload.priceReviewNotes = reviewData.priceReviewNotes
      }
      if (reviewData.criteriaReviewNotes) {
        payload.criteriaReviewNotes = reviewData.criteriaReviewNotes
      }
      if (reviewData.wonNotes) {
        payload.wonNotes = reviewData.wonNotes
      }
      if (reviewData.lostNotes) {
        payload.lostNotes = reviewData.lostNotes
      }
      if (reviewData.tenderCompletedNotes) {
        payload.tenderCompletedNotes = reviewData.tenderCompletedNotes
      }
    }

    console.log('📦 Payload:', payload)

    // Use tender.id (UUID) instead of t247Id for the API call
    const response = await api.put(`/api/tenders/${tender.value.id}/workflow-status`, payload)

    console.log('✅ API Response:', response.data)

    if (response.data.success) {
      tender.value.workflowStatus = workflowStatus

      // Update notes in local state based on workflow status
      if (workflowStatus === 'under-query' && reviewData?.underQueryNotes) {
        tender.value.underQueryNotes = reviewData.underQueryNotes
      } else if (workflowStatus === 'completed' && reviewData?.tenderCompletedNotes) {
        tender.value.tenderCompletedNotes = reviewData.tenderCompletedNotes
      } else if (workflowStatus === 'submitted' && notes) {
        tender.value.agentSubmissionNotes = notes
      } else if (reviewData?.priceReviewNotes) {
        tender.value.priceReviewNotes = reviewData.priceReviewNotes
      } else if (reviewData?.criteriaReviewNotes) {
        tender.value.criteriaReviewNotes = reviewData.criteriaReviewNotes
      } else if (reviewData?.wonNotes) {
        tender.value.wonNotes = reviewData.wonNotes
      } else if (reviewData?.lostNotes) {
        tender.value.lostNotes = reviewData.lostNotes
      } else if (notes && authStore.user?.userType === 'admin') {
        tender.value.adminReviewNotes = notes
      } else if (notes && authStore.user?.userType === 'agent') {
        tender.value.agentNotes = notes
      }

      notificationService.success('Workflow status updated successfully!')

      // Refresh the tender data
      await fetchTenderDetails()
    } else {
      // Handle API error response
      notificationService.error(response.data.message || 'Failed to update workflow status')
    }
  } catch (err: unknown) {
    console.error('❌ Failed to update workflow status:', err)

    // Type guard for AxiosError
    if (err instanceof AxiosError) {
      console.error('❌ Error details:', err.response?.data)

      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error ||
                          err.message ||
                          'Failed to update workflow status'

      notificationService.error(errorMessage)
    } else if (err instanceof Error) {
      // Handle regular Error objects
      notificationService.error(err.message || 'Failed to update workflow status')
    } else {
      // Handle unknown error types
      notificationService.error('Failed to update workflow status')
    }
  }
}

// Refresh tender data
const refreshTenderData = () => {
  fetchTenderDetails()
}

// Handle checklist update
const handleChecklistUpdate = () => {
  console.log('Checklist updated')
  notificationService.success('Compliance checklist updated successfully')
  // Optionally refresh data if needed
  // refreshTenderData()
}

// Computed properties for conditional rendering
const isAdmin = computed(() => authStore.user?.userType === 'admin')
const isAgent = computed(() => authStore.user?.userType === 'agent')
const isEngineer = computed(() => authStore.user?.userType === 'engineer')

// Check if TenderAgentAssignment should be visible
const showAgentAssignment = computed(() => {
  if (!tender.value) return false

  // Show to admin always (for assignment)
  if (isAdmin.value) return true

  // Show to agent only if tender is assigned to them
  if (isAgent.value) {
    const isAssignedToMe = tender.value.assignedToAgent === authStore.user?.id
    return isAssignedToMe
  }

  // Don't show to engineers or other user types
  return false
})

// Check if Engineer View should be visible
const showEngineerView = computed(() => {
  if (!tender.value) return false

  // Show for engineers (they will see only their assigned consignees)
  if (isEngineer.value) return true

  // Also show for admins to see what engineers see
  if (isAdmin.value) return true

  return false
})

onMounted(() => {
  fetchTenderDetails()
})
</script>

<template>
  <AdminLayout>
    <div class="space-y-6">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <LoaderDefault title="Loading tender details..." />
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-12">
        <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <div class="text-red-600 mb-4">
            <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">Failed to Load Tender</h3>
          <p class="text-gray-600 mb-4">{{ error }}</p>
          <div class="flex justify-center gap-2">
            <BaseButton
              @click="fetchTenderDetails"
              text="Try Again"
              size="sm"
            />
            <BaseButton
              @click="router.push('/dashboard')"
              text="Go to Dashboard"
              size="sm"
              variant="secondary"
            />
          </div>
        </div>
      </div>

      <!-- Tender Content -->
      <div v-else-if="tender" class="space-y-6">
        <!-- Refresh Button -->
        <div class="flex justify-end">
          <BaseButton
            @click="refreshTenderData"
            text="Refresh"
            size="sm"
            :icon="RefreshIcon"
            variant="secondary"
          />
        </div>

        <!-- Tender Header -->
        <TenderHeader
          :tender="tender"
        />

        <!-- Main Content Grid -->
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <!-- Left Column - Tender Overview & Documents -->
          <div :class="isEngineer ? 'xl:col-span-3' : 'lg:col-span-2'" class="space-y-6">
            <!-- Tender Overview -->
            <TenderOverView
              :tender="tender"
            />

            <!-- Compliance Checklist Section -->
            <TenderComplianceChecklist
              v-if="showAgentAssignment"
              :tenderId="tenderId"
              :tenderStatus="tender.status"
              :tenderData="{
                organization: tender.organization,
                departmentName: tender.organization || '',
                referenceNumber: tender.referenceNo || '',
                referenceNo: tender.referenceNo || '',
                tenderBrief: tender.tenderBrief || '',
                departmentAddress: tender.organization || ''
              }"
              :userCompany="userCompany"
              @checklist-updated="handleChecklistUpdate"
            />

            <!-- ✅ FIX: Only render TenderEngineerView when tender.id exists -->
            <TenderEngineerView
              v-if="showEngineerView && tender.id"
              :tenderId="tender.id"
            />
          </div>

          <!-- Right Column - Assignment & Status -->
          <div class="space-y-6">
            <!-- Agent Assignment Section (Only show to Admin and assigned Agents) -->
            <TenderAgentAssignment
              v-if="showAgentAssignment"
              :tender="tender"
              @workflow-update="handleAgentAssignment"
              @tender-assigned="refreshTenderData"
              @refresh-tender="refreshTenderData"
            />

            <!-- Status Section -->
            <TenderStatus
              v-if="showAgentAssignment"
              :tender="tender"
              @workflow-updated="handleWorkflowUpdate"
              @status-updated="updateTenderStatus"
            />

            <TenderAuditTrail
              v-if="showAgentAssignment"
              :tender-id="tenderId"
            />
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>
